import React, { memo, useEffect, useMemo, useState } from 'react';
import { fetchSynthetics, fetchSyntheticsPrice } from '../../utils';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import * as actionTypes from '../../store/actionTypes';
import { usePoll } from '../../hooks/usePoll';
import TokenTable from '../../components/Tables/TokenTable/token.table';
import { useTokenListing, ListingFooter } from '../../hooks/useTokenListing';

const SyntheticTable = ({ searchToken }) => {
  const dispatch = useDispatch();
  const { PecuPrice, synthetics, syntheticsPrice, syntheticVolumes } =
    useSelector(
      (state) => ({
        PecuPrice: state.PecuPrice,
        synthetics: state.synthetics,
        syntheticsPrice: state.syntheticsPrice,
        syntheticVolumes: state.syntheticVolumes
      }),
      shallowEqual
    );

  const [priceList, setPriceList] = useState([]);
  const [blinkUpList, setBlinkUpList] = useState([]);
  const [blinkDownList, setBlinkDownList] = useState([]);
  const [pecuPricePrev, setPecuPricePrev] = useState(null);
  const [pecuBlinkUp, setPecuBlinkUp] = useState(false);
  const [pecuBlinkDown, setPecuBlinkDown] = useState(false);
  const [syntheticsPriceTick, setSyntheticsPriceTick] =
    useState(syntheticsPrice);

  // PECU Price Blink Effect
  useEffect(() => {
    if (pecuPricePrev != null) {
      if (PecuPrice > pecuPricePrev) {
        setPecuBlinkUp(true);
        setPecuBlinkDown(false);
      } else if (PecuPrice < pecuPricePrev) {
        setPecuBlinkDown(true);
        setPecuBlinkUp(false);
      } else {
        setPecuBlinkUp(false);
        setPecuBlinkDown(false);
      }

      setTimeout(() => {
        setPecuBlinkUp(false);
        setPecuBlinkDown(false);
      }, 500);
    }
    setPecuPricePrev(PecuPrice);
  }, [PecuPrice]);

  // Stats (trend, TVL, volume) are now fetched globally in App.js every 30 s
  // and stored in Redux.state.stats — TokenTable reads them from there directly.

  // Track blink up/down tokens
  useEffect(() => {
    if (!syntheticsPriceTick || Object.keys(syntheticsPriceTick).length === 0)
      return;

    if (priceList.length === 0) {
      setPriceList(
        Object.entries(syntheticsPrice).map(([token, price]) => ({
          token,
          price
        }))
      );
      return;
    }

    const updatedPrices = Object.entries(syntheticsPriceTick)
      .filter(([token, price]) => {
        const prev = priceList.find((p) => p.token === token);
        return prev && prev.price !== price;
      })
      .map(([token, price]) => ({ token, price }));

    if (updatedPrices.length) {
      const blinkUp = [];
      const blinkDown = [];

      updatedPrices.forEach(({ token, price }) => {
        const prev = priceList.find((p) => p.token === token);
        if (!prev) return;
        if (price > prev.price) blinkUp.push(token);
        else if (price < prev.price) blinkDown.push(token);
      });

      setPriceList((prev) => [
        ...prev.filter((p) => !updatedPrices.some((u) => u.token === p.token)),
        ...updatedPrices
      ]);
      setBlinkUpList(blinkUp);
      setBlinkDownList(blinkDown);

      setTimeout(() => {
        setBlinkUpList([]);
        setBlinkDownList([]);
      }, 500);
    }
  }, [syntheticsPriceTick]);

  // Main data loader — fetch once on mount (no polling interval needed for synthetics list)
  useEffect(() => {
    fetchSynthetics(dispatch, actionTypes.FETCH_SYNTHETICS);
  }, []);

  usePoll(
    () => fetchSyntheticsPrice(dispatch, actionTypes.FETCH_SYNTHETICS_PRICE),
    5000,
    []
  );

  useEffect(() => {
    setSyntheticsPriceTick(syntheticsPrice);
  }, [syntheticsPrice]);

  const getFormattedTime = (timestampString) => {
    const date = new Date(timestampString);
    return date;
  };

  // Full list, sorted; search + pagination run over all of it.
  const sortedTokens = useMemo(
    () =>
      synthetics
        ? [...synthetics].sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
        : [],
    [synthetics]
  );
  const listing = useTokenListing(sortedTokens, searchToken);
  // The PECU row stays pinned on top, but only when it matches the search.
  const showPecuRow =
    listing.page === 1 &&
    (!listing.query || 'pecu novus'.includes(listing.query.toLowerCase()));

  return (
    <>
    <TokenTable
      loading={false}
      token={listing.pageRows}
      blinkUpList={blinkUpList}
      blinkDownList={blinkDownList}
      href="SynthCryptos"
      PecuPrice={PecuPrice}
      swappable
      getFormattedTime={getFormattedTime}
      showPecu={showPecuRow}
      priceData={syntheticsPrice}
      volumes={syntheticVolumes}
      pecuBlinkUp={pecuBlinkUp}
      pecuBlinkDown={pecuBlinkDown}
    />
    <ListingFooter
      listing={listing}
      loaded={synthetics?.length > 0}
      noun="SynthCryptos"
    />
    </>
  );
};

export default React.memo(SyntheticTable);
